import { useEffect, useRef, useState } from 'react';
import { IntersectionOptions, useInView } from 'react-intersection-observer';
import {
  WistiaPlayerElement,
  WistiaPlayer,
  WistiaPlayerProps,
} from '@wistia/wistia-player-react';
import { cn } from '@/shadcn-ui/utils';

interface FlexcarIn75SecondsVideoProps
  extends Omit<WistiaPlayerProps, 'ref' | 'mediaId' | 'onMuteChange'> {
  containerClassName?: string;
  /**
   * Options for the intersection observer.
   * @example {
   *   rootMargin: '100px 0px',
   * }
   */
  intersectionOptions?: IntersectionOptions;
  /**
   * The fit strategy for the video.
   * @see https://docs.wistia.com/docs/embed-options-and-plugins#fitstrategy
   */
  fitStrategy?: 'contain' | 'cover' | 'fill' | 'none';
}

/**
 * Renders the "Flexcar In 75 Seconds" Wistia video. The video plays silently when it's in the viewport
 * and pauses when it's out of view. Once a user unmutes the video, control is back given to the user.
 */
export default function FlexcarIn75SecondsVideo({
  // Container props
  containerClassName,
  intersectionOptions,
  // WistiaPlayer props
  aspect = 16 / 9,
  roundedPlayer = 16,
  silentAutoplay = true,
  autoplay = true,
  controlsVisibleOnLoad = false,
  endVideoBehavior = 'reset',
  preload = 'auto',
  playerColor = '#0B0C0E',
  className = 'w-full',
  ...wistiaProps
}: FlexcarIn75SecondsVideoProps) {
  const [hasUnmuted, setHasUnmuted] = useState(false);
  const wistia = useRef<WistiaPlayerElement | null>(null);

  const { ref, inView } = useInView({
    // This will play the video 500px before the video first enters the viewport
    rootMargin: '100px 0px',
    ...intersectionOptions,
  });

  useEffect(() => {
    // If the user has interacted with the player (unmuted), relinquish control to the user
    if (hasUnmuted) return;

    const player = wistia.current;
    if (!player) return;

    // Play when in view, pause when out of view
    if (inView) {
      player.play?.();
    } else {
      player.pause?.();
    }
  }, [hasUnmuted, inView]);

  return (
    <div ref={ref} className={cn(containerClassName, 'w-full')}>
      <WistiaPlayer
        ref={wistia}
        mediaId="d0vr05zkru"
        onMuteChange={(event) => {
          const isMuted = event.detail.isMuted;
          if (!hasUnmuted && !isMuted) {
            setHasUnmuted(true);
          }
        }}
        aspect={aspect}
        roundedPlayer={roundedPlayer}
        silentAutoplay={silentAutoplay}
        autoplay={autoplay}
        controlsVisibleOnLoad={controlsVisibleOnLoad}
        endVideoBehavior={endVideoBehavior}
        preload={preload}
        playerColor={playerColor}
        className={cn(className, 'w-full')}
        {...wistiaProps}
      />
    </div>
  );
}
